import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Mail, Send, Facebook, Instagram } from "lucide-react";
import { trackWhatsApp, trackSocialClick, trackFormSubmit } from "../lib/analytics";
import { useLang } from "../context/LanguageContext";
import { GRADES } from "../i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  parent_name: "",
  child_name: "",
  child_age: "",
  grade: "",
  email: "",
  phone: "",
  message: "",
};

const Contact = () => {
  const { t } = useLang();
  const f = t.contact.form;
  const info = t.contact.info;
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/preregistrations`, form);
      toast.success(f.success);
      trackFormSubmit({ grade: form.grade || "unknown" });
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : f.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="relative py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 md:gap-16 items-start">
          <div className="lg:col-span-2">
            <div className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[#E63946] mb-4">
              {t.contact.overline}
            </div>
            <h2 className="font-fredoka text-4xl md:text-5xl font-semibold leading-[1.05] text-[#1D3557]">
              {t.contact.title}
            </h2>
            <p className="mt-6 text-lg text-[#457B9D] leading-relaxed">{t.contact.subtitle}</p>

            <div className="mt-10 space-y-4">
              <a
                href="tel:+524422342114"
                data-testid="contact-phone"
                className="flex items-center gap-4 p-5 rounded-2xl bg-[#FDFBF7] hover:bg-white border border-[#E2E8F0]/70 hover:border-[#1E5FBA] transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#1E5FBA]/10 grid place-items-center group-hover:bg-[#1E5FBA] transition-colors">
                  <Phone className="w-5 h-5 text-[#1E5FBA] group-hover:text-white transition-colors" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#457B9D] font-semibold">{info.phoneLabel}</div>
                  <div className="font-fredoka text-lg text-[#1D3557] font-semibold">{info.phone}</div>
                </div>
              </a>
              <a
                href="https://wa.me/524421182816"
                target="_blank"
                rel="noreferrer"
                onClick={() => trackWhatsApp("contact_card")}
                data-testid="contact-whatsapp"
                className="flex items-center gap-4 p-5 rounded-2xl bg-[#FDFBF7] hover:bg-white border border-[#E2E8F0]/70 hover:border-[#2E9E4E] transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#2E9E4E]/20 grid place-items-center group-hover:bg-[#2E9E4E] transition-colors">
                  <MessageCircle className="w-5 h-5 text-[#1F7437] group-hover:text-white transition-colors" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#457B9D] font-semibold">{info.whatsappLabel}</div>
                  <div className="font-fredoka text-lg text-[#1D3557] font-semibold">{info.whatsapp}</div>
                </div>
              </a>
              <a
                href={`mailto:${info.email}`}
                data-testid="contact-email"
                className="flex items-center gap-4 p-5 rounded-2xl bg-[#FDFBF7] hover:bg-white border border-[#E2E8F0]/70 hover:border-[#E63946] transition-colors group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#E63946]/10 grid place-items-center group-hover:bg-[#E63946] transition-colors">
                  <Mail className="w-5 h-5 text-[#E63946] group-hover:text-white transition-colors" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#457B9D] font-semibold">{info.emailLabel}</div>
                  <div className="font-fredoka text-base text-[#1D3557] font-semibold break-all">{info.email}</div>
                </div>
              </a>

              {/* Social media */}
              <div className="flex items-center gap-3 pt-3 pl-1">
                <span className="text-xs uppercase tracking-widest text-[#457B9D] font-semibold">
                  {t.contact.info.socialLabel}
                </span>
                <a
                  data-testid="contact-facebook"
                  href="https://www.facebook.com/share/1Jjt28LN1n/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackSocialClick("facebook", "contact")}
                  aria-label="Facebook"
                  className="w-10 h-10 rounded-full bg-[#1E5FBA] text-white grid place-items-center hover:bg-[#1D3557] hover:scale-110 transition-all shadow-md"
                >
                  <Facebook className="w-4 h-4" strokeWidth={2.2} />
                </a>
                <a
                  data-testid="contact-instagram"
                  href="https://www.instagram.com/kinder_steps"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackSocialClick("instagram", "contact")}
                  aria-label="Instagram"
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white grid place-items-center hover:scale-110 transition-transform shadow-md"
                >
                  <Instagram className="w-4 h-4" strokeWidth={2.2} />
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-[#FDFBF7] rounded-[2rem] p-8 md:p-10 border border-[#E2E8F0]/70 shadow-[0_20px_50px_rgba(29,53,87,0.08)]"
          >
            <a
              href="https://wa.me/524421182816"
              target="_blank"
              rel="noreferrer"
              data-testid="form-quick-call"
              className="flex items-center justify-between gap-4 mb-6 p-4 rounded-2xl bg-[#1E5FBA]/8 border-2 border-[#1E5FBA]/15 hover:border-[#1E5FBA]/40 hover:bg-[#1E5FBA]/12 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#25D366] grid place-items-center shrink-0">
                  <MessageCircle className="w-5 h-5 text-white" strokeWidth={2.75} />
                </div>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-widest text-[#457B9D] font-bold">{t.contact.quickContactLabel}</div>
                  <div className="font-fredoka text-base text-[#1D3557] font-semibold">{t.contact.quickContactText} <span className="text-[#1E5FBA]">442 118 28 16</span></div>
                </div>
              </div>
              <span className="text-[#1E5FBA] text-xl group-hover:translate-x-1 transition-transform">→</span>
            </a>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label={f.parentName} name="parent_name" value={form.parent_name} onChange={onChange} required testid="input-parent-name" />
              <Field label={f.childName} name="child_name" value={form.child_name} onChange={onChange} required testid="input-child-name" />
              <Field label={f.childAge} name="child_age" value={form.child_age} onChange={onChange} required testid="input-child-age" placeholder="3" />
              <div>
                <label className="block text-xs font-bold mb-2 text-[#1D3557] ml-1 uppercase tracking-wide">
                  {f.grade}
                </label>
                <select
                  name="grade"
                  value={form.grade}
                  onChange={onChange}
                  required
                  data-testid="input-grade"
                  className="w-full rounded-2xl bg-white border-2 border-transparent focus:border-[#1E5FBA] px-5 py-3.5 outline-none transition-all text-[#1D3557]"
                >
                  <option value="">{f.selectGrade}</option>
                  {GRADES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              <Field label={f.email} name="email" type="email" value={form.email} onChange={onChange} required testid="input-email" />
              <Field label={f.phone} name="phone" type="tel" value={form.phone} onChange={onChange} required testid="input-phone" />
              <div className="md:col-span-2">
                <label className="block text-xs font-bold mb-2 text-[#1D3557] ml-1 uppercase tracking-wide">
                  {f.message}
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  data-testid="input-message"
                  className="w-full rounded-2xl bg-white border-2 border-transparent focus:border-[#1E5FBA] px-5 py-3.5 outline-none transition-all text-[#1D3557] resize-none"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-form-submit"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#1E5FBA] hover:bg-[#164A94] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 font-semibold shadow-[0_12px_30px_rgba(30,95,186,0.35)] hover:-translate-y-0.5 transition-all"
            >
              <Send className="w-4 h-4" strokeWidth={2.5} />
              {loading ? f.sending : f.submit}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required, testid, placeholder }) => (
  <div>
    <label className="block text-xs font-bold mb-2 text-[#1D3557] ml-1 uppercase tracking-wide">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full rounded-2xl bg-white border-2 border-transparent focus:border-[#1E5FBA] px-5 py-3.5 outline-none transition-all text-[#1D3557] placeholder:text-[#94A3B8]"
    />
  </div>
);

export default Contact;
